/**
 * Minimal RFC-4180 CSV parsing for #782 bulk client import. Handles quoted
 * fields (embedded commas / newlines), escaped `""` quotes, and CRLF or LF line
 * endings. Kept tiny on purpose — no dependency for a header + a handful of
 * columns. The header row is lowercased/trimmed; cell values are trimmed.
 */

export type CsvRow = Record<string, string>;

/** Tokenize CSV text into rows of raw string fields. */
function tokenize(text: string): string[][] {
  const rows: string[][] = [];
  let field = "";
  let row: string[] = [];
  let inQuotes = false;

  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++; // consume the escaped quote
        } else {
          inQuotes = false;
        }
      } else {
        field += c;
      }
      continue;
    }
    if (c === '"') {
      inQuotes = true;
    } else if (c === ",") {
      row.push(field);
      field = "";
    } else if (c === "\r") {
      // ignore — handled by the \n branch (CRLF) or a bare CR is dropped
    } else if (c === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else {
      field += c;
    }
  }
  // trailing field/row when the text has no final newline
  if (field !== "" || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

/**
 * Parse CSV text into an array of objects keyed by the (lowercased) header row.
 * Fully-blank lines are skipped. Returns `[]` when there is no data row.
 */
export function parseCsv(text: string): CsvRow[] {
  const rows = tokenize(text);
  if (rows.length === 0) return [];
  const header = rows[0].map((h) => h.trim().toLowerCase());
  return rows
    .slice(1)
    .filter((cells) => cells.some((c) => c.trim() !== ""))
    .map((cells) => {
      const obj: CsvRow = {};
      header.forEach((key, i) => {
        if (key) obj[key] = (cells[i] ?? "").trim();
      });
      return obj;
    });
}
